import React, { useEffect, useRef, useState } from "react";
import { Redirect, useLocation } from "react-router-dom";
import { IoChevronForward, IoTrash } from "react-icons/io5";
import {
  mdiCheckboxMarkedCircle,
  mdiMessageOutline,
  mdiMessageReply,
  mdiPhone,
  mdiPhoneInTalk,
  mdiRadioboxBlank,
} from "@mdi/js";
import { MODELS, TRAITS, isModel } from "../../../constants/devices";
import { sendControl } from "../../../services/deviceControl";
import { UserService } from "../../../services/UserService";
import { useToggleTop } from "../../../components/common/ToggleTop";
import AppModal from "../../../components/layout/app/AppModal";
import Sheet from "../../../components/layout/app/Sheet";
import MdiIcon from "../../../components/device/app/MdiIcon";
import {
  DEFAULT_ERROR,
  NumberField,
  SettingsPage,
  SettingsSkeleton,
  Unavailable,
  inRange,
  settingsPayload,
  toText,
  useAlive,
} from "./shared";
import "../../../assets/styles/app-settings.css";
import "../../../assets/styles/app-security-settings.css";

const SETTINGS = Number(TRAITS.SECURITY_SETTINGS);
const NUMBERS = Number(TRAITS.SECURITY_NUMBERS);

// Trait 1305 ids of the rows that open a sheet (each switch carries its own),
// and the trait 1306 ids that store and clear a number.
const SETTING_IDS = { delays: 1, wiredType: 5, sirenDuration: 8, alarmNumber: 9 };
const NUMBER_IDS = { save: 2, remove: 3 };

// The app's toasts on this screen. Its switches and pickers say nothing when a
// command fails; this page shows DEFAULT_ERROR for those.
const SAVED = "Updated successfully!";
const ERRORS = { failed: DEFAULT_ERROR, empty: DEFAULT_ERROR };
const NUMBER_ERRORS = { failed: "An error occurred while updating.", empty: "Update failed. Please try again." };

// The app's wheels for the siren time and the delays run from 0 to 299 s.
const MAX_SECONDS = 299;
const SECONDS_PLACEHOLDER = `0-${MAX_SECONDS} sec`;
const SECONDS_HINT = `Range from 0 to ${MAX_SECONDS} Seconds`;

// Stored numbers must be Indian mobile numbers, as in the app.
const PHONE = /^[6-9]\d{9}$/;
const PHONE_ERROR = "Enter a valid 10-digit Indian phone number.";

// The on/off rows in the app's order. Each sends trait 1305 with its id and 0 or 1.
const SWITCHES = [
  {
    key: "door_monitor",
    id: 4,
    title: "Door Sensor Monitoring",
    description: "Get a notification if a door or window is left open after arming the alarm system.",
    image: "door_sensor",
  },
  {
    key: "alarm",
    id: 6,
    title: "Alarm Type",
    description: "Users can control if the siren will sound or stay quiet during an alert.",
    image: "alarm",
  },
  { key: "local_siren", id: 7, title: "Local Siren", image: "siren" },
  {
    key: "gsm_monitor",
    id: 2,
    title: "GSM Signal Monitoring",
    // The app repeats the door sensor's text here.
    description: "Get a notification if a door or window is left open after arming the alarm system.",
    image: "gsm",
  },
  {
    key: "remote_alert",
    id: 3,
    title: "Remote Control Armed/Disarmed Alert",
    description: "Be notified of who is managing the system via remote control.",
    image: "remote",
  },
];

const WIRED_TYPES = [
  { value: 0, label: "N.O" },
  { value: 1, label: "N.C" },
];

// The gateway's eight number slots in its order: num_index is the position + 1.
// The settings hold them as sms_1-3 and phone_1-5.
const SLOTS = [
  ...[1, 2, 3].map((n) => ({ key: `sms_${n}`, name: `SMS alert number ${n}` })),
  ...[1, 2, 3, 4, 5].map((n) => ({ key: `phone_${n}`, name: `Alarm call number ${n}` })),
];

const NUMBER_SECTIONS = [
  {
    id: "sms",
    title: "Add phone numbers to receive SMS alerts",
    icon: mdiMessageReply,
    slotIcon: mdiMessageOutline,
    slots: [0, 1, 2],
  },
  {
    id: "call",
    title: "Add phone numbers to receive alarm calls",
    icon: mdiPhoneInTalk,
    slotIcon: mdiPhone,
    slots: [3, 4, 5, 6, 7],
  },
];

const SIREN_FORM = "hx-set-sec-siren-form";
const SIREN_INPUT = "hx-set-sec-siren";
const DELAY_FORM = "hx-set-sec-delay-form";
const ALARM_FORM = "hx-set-sec-alarm-form";
const ALARM_INPUT = "hx-set-sec-alarm-number";
const NUMBER_FORM = "hx-set-sec-number-form";
const NUMBER_INPUT = "hx-set-sec-number";
const NUMBER_ERROR = "hx-set-sec-number-error";

const DELAYS = [
  { key: "exit", label: "Exit Delay in Seconds" },
  { key: "entry", label: "Entry Delay in Seconds" },
];
const delayId = (key) => `hx-set-sec-${key}-delay`;

// The page's values from the gateway's settings, with the app's defaults for
// any that are missing, so a gateway that hasn't reported yet can still be set up.
const readSettings = (saved) => {
  const settings = saved && typeof saved === "object" ? saved : {};
  const values = {
    wired_type: String(settings.wired_type || "0") === "0" ? 0 : 1,
    siren_duration: toText(settings.siren_duration || "0"),
    alarm_phone_number: toText(settings.alarm_phone_number),
    exit_delay: toText(settings.exit_delay || "0"),
    entry_delay: toText(settings.entry_delay || "0"),
    numbers: SLOTS.map((slot) => toText(settings[slot.key])),
  };
  SWITCHES.forEach((item) => {
    values[item.key] = Boolean(Number(settings[item.key]));
  });
  return values;
};

// Stores or clears one slot's number in the page's values.
const withNumber = (slot, number) => (current) => {
  const numbers = current.numbers.slice();
  numbers[slot] = number;
  return { ...current, numbers };
};

const digits = (text) => text.replace(/\D/g, "");

// Puts the cursor in a dialog's box, like the app's autoFocus. Called by the
// component that renders the dialog, so it runs after the dialog has noted
// where to send focus back to when it closes.
const useFocusField = (id) => {
  useEffect(() => {
    document.getElementById(id)?.focus();
  }, [id]);
};

// A row's image from the app, tinted in its security red by the stylesheet.
const RowIcon = ({ name }) => (
  <span
    className="hx-set-sec-icon"
    style={{ "--hx-set-sec-icon": `url(/security_device/${name}.png)` }}
    aria-hidden="true"
  />
);

// An on/off row. The title labels the switch and the text under it describes it.
const SwitchRow = ({ item, checked, onToggle }) => {
  const id = `hx-set-sec-${item.key}`;
  const descriptionId = item.description ? `${id}-description` : undefined;
  return (
    <li className="hx-set-sec-row">
      <RowIcon name={item.image} />
      <span className="hx-set-sec-text">
        <label className="hx-set-sec-title" htmlFor={id}>
          {item.title}
        </label>
        {item.description ? (
          <span id={descriptionId} className="hx-set-sec-description">
            {item.description}
          </span>
        ) : null}
      </span>
      <span className="hx-set-sec-switch">
        <input
          id={id}
          type="checkbox"
          role="switch"
          checked={checked}
          aria-describedby={descriptionId}
          onChange={onToggle}
        />
        <span className={`hx-switch ${checked ? "is-on" : ""}`} aria-hidden="true">
          <span className="hx-switch-knob" />
        </span>
      </span>
    </li>
  );
};

// A row that opens a sheet or dialog, with the current value where the app shows one.
const LinkRow = ({ image, title, value, onOpen }) => (
  <li>
    <button type="button" className="hx-set-sec-row" aria-haspopup="dialog" onClick={onOpen}>
      <RowIcon name={image} />
      <span className="hx-set-sec-text">
        <span className="hx-set-sec-title">{title}</span>
      </span>
      {value ? <span className="hx-set-sec-value">{value}</span> : null}
      <IoChevronForward aria-hidden="true" />
    </button>
  </li>
);

// "Save" in a sheet's header. It submits the sheet's form.
const SaveButton = ({ form }) => (
  <button type="submit" form={form} className="hx-set-sheet-save">
    Save
  </button>
);

// Wired Sensor Type. As in the app, choosing a type sends it straight away.
const WiredSheet = ({ value, onClose, onPick }) => (
  <Sheet open onClose={onClose} title="Wired Sensor Type">
    <div className="hx-set-sec-options">
      {WIRED_TYPES.map((option) => {
        const picked = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            className="hx-set-sec-option"
            aria-pressed={picked}
            onClick={() => onPick(option.value)}
          >
            <MdiIcon path={picked ? mdiCheckboxMarkedCircle : mdiRadioboxBlank} size={30} />
            {option.label}
          </button>
        );
      })}
    </div>
  </Sheet>
);

// Siren Duration Time. The app's 0-299 s wheel becomes a number box.
const SirenSheet = ({ value, onClose, onSave }) => {
  const [seconds, setSeconds] = useState(value);
  const [invalid, setInvalid] = useState(false);

  const submit = (event) => {
    event.preventDefault();
    if (!inRange(seconds, 0, MAX_SECONDS)) {
      setInvalid(true);
      document.getElementById(SIREN_INPUT)?.focus();
      return;
    }
    onSave(Number(seconds));
  };

  return (
    <Sheet open onClose={onClose} title="Siren Duration Time" actions={<SaveButton form={SIREN_FORM} />}>
      <form id={SIREN_FORM} className="hx-set-sec-sheet-form" noValidate onSubmit={submit}>
        <NumberField
          id={SIREN_INPUT}
          label="in Seconds"
          value={seconds}
          placeholder={SECONDS_PLACEHOLDER}
          hint={SECONDS_HINT}
          invalid={invalid}
          onChange={(next) => {
            setSeconds(next);
            setInvalid(false);
          }}
        />
      </form>
    </Sheet>
  );
};

// Exit / Entry Delay. The app's two wheels become number boxes, saved together.
const DelaySheet = ({ exit, entry, onClose, onSave }) => {
  const [delays, setDelays] = useState({ exit, entry });
  const [invalid, setInvalid] = useState(null);

  const submit = (event) => {
    event.preventDefault();
    const problem = DELAYS.find(({ key }) => !inRange(delays[key], 0, MAX_SECONDS));
    if (problem) {
      setInvalid(problem.key);
      document.getElementById(delayId(problem.key))?.focus();
      return;
    }
    onSave({ exit: Number(delays.exit), entry: Number(delays.entry) });
  };

  return (
    <Sheet open onClose={onClose} title="Exit/Entry Delay" actions={<SaveButton form={DELAY_FORM} />}>
      <form id={DELAY_FORM} className="hx-set-sec-sheet-form hx-set-sec-delays" noValidate onSubmit={submit}>
        {DELAYS.map(({ key, label }) => (
          <NumberField
            key={key}
            id={delayId(key)}
            label={label}
            value={delays[key]}
            placeholder={SECONDS_PLACEHOLDER}
            hint={SECONDS_HINT}
            invalid={invalid === key}
            onChange={(next) => {
              setDelays((current) => ({ ...current, [key]: next }));
              if (invalid === key) setInvalid(null);
            }}
          />
        ))}
      </form>
    </Sheet>
  );
};

// Phone Number of Alarm System: up to 14 digits, and Save needs at least 10.
const AlarmNumberDialog = ({ value, onClose, onSave }) => {
  const [number, setNumber] = useState(value);
  const ready = number.length >= 10;
  useFocusField(ALARM_INPUT);

  const submit = (event) => {
    event.preventDefault();
    if (ready) onSave(number);
  };

  return (
    <AppModal
      open
      onClose={onClose}
      title="Phone Numbers of alarm system"
      actions={
        <>
          <button type="button" className="hx-btn is-ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" form={ALARM_FORM} className="hx-btn" disabled={!ready}>
            Save
          </button>
        </>
      }
    >
      <form id={ALARM_FORM} noValidate onSubmit={submit}>
        <label className="hx-set-sr" htmlFor={ALARM_INPUT}>
          Phone Number of Alarm System
        </label>
        <input
          id={ALARM_INPUT}
          className="hx-input hx-set-sec-phone"
          type="tel"
          inputMode="numeric"
          autoComplete="off"
          placeholder="Enter Phone Number"
          value={number}
          onChange={(event) => setNumber(digits(event.target.value).slice(0, 14))}
        />
      </form>
    </AppModal>
  );
};

// Store alarm phone Numbers: the SMS and call slots. Choosing one opens its editor.
const NumbersSheet = ({ numbers, onClose, onEdit }) => (
  <Sheet open onClose={onClose} title="Store alarm phone Numbers" tinted>
    {NUMBER_SECTIONS.map((section) => (
      <div key={section.id} className="hx-set-sec-group">
        <h3 className="hx-set-sec-group-title">
          <MdiIcon path={section.icon} size={24} />
          {section.title}
        </h3>
        <ul className="hx-set-sec-slots">
          {section.slots.map((slot, index) => {
            const number = numbers[slot];
            return (
              <li key={slot}>
                <button type="button" className="hx-set-sec-slot" aria-haspopup="dialog" onClick={() => onEdit(slot)}>
                  <span className="hx-set-sec-slot-tag" aria-hidden="true">
                    <MdiIcon path={section.slotIcon} size={20} />
                    {index + 1}
                  </span>
                  <span className="hx-set-sr">{SLOTS[slot].name}: </span>
                  {number ? (
                    <span className="hx-set-sec-slot-value">{number}</span>
                  ) : (
                    <span className="hx-set-sec-slot-value is-empty">
                      <span aria-hidden="true">000000000</span>
                      <span className="hx-set-sr">Not set</span>
                    </span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    ))}
  </Sheet>
);

// Edits one stored number. Save needs a valid Indian mobile number; Delete
// clears the number stored in the slot.
const NumberDialog = ({ name, stored, onClose, onSave, onDelete }) => {
  const [number, setNumber] = useState(stored);
  const [edited, setEdited] = useState(false);
  const valid = PHONE.test(number);
  const showError = edited && !valid;
  useFocusField(NUMBER_INPUT);

  const submit = (event) => {
    event.preventDefault();
    if (valid) onSave(number);
  };

  return (
    <AppModal
      open
      onClose={onClose}
      title={name}
      actions={
        <>
          <button type="button" className="hx-btn is-ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" form={NUMBER_FORM} className="hx-btn" disabled={!valid}>
            Save
          </button>
        </>
      }
    >
      <form id={NUMBER_FORM} noValidate onSubmit={submit}>
        <label className="hx-set-sr" htmlFor={NUMBER_INPUT}>
          {name}
        </label>
        <div className="hx-set-sec-phone-row">
          {/* Not cut to 10 digits: a pasted +91 number should show the error,
              not turn into a different number that passes the check. */}
          <input
            id={NUMBER_INPUT}
            className="hx-input hx-set-sec-phone"
            type="tel"
            inputMode="numeric"
            autoComplete="off"
            placeholder="00000-00000"
            value={number}
            aria-invalid={showError || undefined}
            aria-describedby={NUMBER_ERROR}
            onChange={(event) => {
              setNumber(digits(event.target.value));
              setEdited(true);
            }}
          />
          {stored ? (
            <button type="button" className="hx-icon-btn hx-set-sec-delete" aria-label="Delete number" onClick={onDelete}>
              <IoTrash aria-hidden="true" />
            </button>
          ) : null}
        </div>
        <p id={NUMBER_ERROR} className="hx-set-sec-error" aria-live="polite">
          {showError ? PHONE_ERROR : ""}
        </p>
      </form>
    </AppModal>
  );
};

// Settings for the WZLTEG_1 security alarm gateway (app/screens/DeviceSettings/
// SecuritySettings.js): five switches, then the rows that open the wired sensor
// type, siren time, stored numbers, the gateway's own number and the delays.
function SecuritySettings() {
  const { state } = useLocation();
  const toast = useToggleTop();
  const alive = useAlive();
  const device = state?.data;
  const deviceId = device?.deviceId;
  const supported = isModel(device, MODELS.WZLTEG_1) && Boolean(deviceId);
  const [values, setValues] = useState(null);
  const [status, setStatus] = useState("loading");
  const [attempt, setAttempt] = useState(0);
  const [busy, setBusy] = useState(null);
  // The open sheet or dialog: "wired", "siren", "numbers", "alarm" or "delay".
  const [open, setOpen] = useState(null);
  // The number slot (0-7) being edited over the numbers sheet.
  const [slot, setSlot] = useState(null);
  // While a number is being edited, Escape should close only its dialog. The
  // numbers sheet's key handler keeps the onClose it opened with, so it reads
  // this ref.
  const editing = useRef(false);
  editing.current = slot !== null;

  useEffect(() => {
    if (!supported) return undefined;
    let current = true;
    setStatus("loading");
    UserService.getDevice(deviceId)
      .then((response) => {
        if (!current) return;
        setValues(readSettings(response?.[0]?.device?.deviceSettings?.[0]?.settings));
        setStatus("ready");
      })
      .catch(() => {
        if (current) setStatus("error");
      });
    return () => {
      current = false;
    };
  }, [deviceId, supported, attempt]);

  if (!device) return <Redirect to="/dashboard" />;

  // Sends one command behind the loader. The page changes only once the
  // gateway has taken it; like the app, an empty reply counts as a failure.
  const run = async (command, apply, errors = ERRORS) => {
    setBusy("Updating settings…");
    let outcome;
    try {
      outcome = (await sendControl(settingsPayload(device, command))) ? "saved" : "empty";
    } catch (error) {
      outcome = "failed";
    }
    if (alive.current) {
      if (outcome === "saved") setValues(apply);
      setBusy(null);
    }
    if (outcome === "saved") toast(SAVED, "green");
    else toast(errors[outcome], "red");
  };

  const openSheet = (name) => {
    if (!busy) setOpen(name);
  };
  const closeSheet = () => setOpen(null);
  const closeNumbers = () => {
    if (!editing.current) setOpen(null);
  };
  const editSlot = (index) => {
    if (!busy) setSlot(index);
  };

  const toggle = (item) => {
    if (busy) return;
    const next = !values[item.key];
    run({ id: item.id, trait: SETTINGS, value: Number(next) }, (current) => ({ ...current, [item.key]: next }));
  };

  // Choosing the type already set only closes the sheet.
  const pickWired = (value) => {
    setOpen(null);
    if (value === values.wired_type) return;
    run({ id: SETTING_IDS.wiredType, trait: SETTINGS, value }, (current) => ({ ...current, wired_type: value }));
  };

  const saveSiren = (seconds) => {
    setOpen(null);
    run({ id: SETTING_IDS.sirenDuration, trait: SETTINGS, value: seconds }, (current) => ({
      ...current,
      siren_duration: String(seconds),
    }));
  };

  const saveAlarmNumber = (number) => {
    setOpen(null);
    run({ id: SETTING_IDS.alarmNumber, trait: SETTINGS, value: number }, (current) => ({
      ...current,
      alarm_phone_number: number,
    }));
  };

  const saveDelays = (delays) => {
    setOpen(null);
    run({ id: SETTING_IDS.delays, trait: SETTINGS, value: delays }, (current) => ({
      ...current,
      exit_delay: String(delays.exit),
      entry_delay: String(delays.entry),
    }));
  };

  // The numbers sheet stays open so the change shows in its list.
  const saveNumber = (number) => {
    const index = slot;
    setSlot(null);
    run(
      { id: NUMBER_IDS.save, trait: NUMBERS, value: { num_index: index + 1, num: number } },
      withNumber(index, number),
      NUMBER_ERRORS
    );
  };

  const deleteNumber = () => {
    const index = slot;
    setSlot(null);
    run({ id: NUMBER_IDS.remove, trait: NUMBERS, value: index + 1 }, withNumber(index, ""), NUMBER_ERRORS);
  };

  let body;
  if (!supported) {
    body = <Unavailable />;
  } else if (status === "loading") {
    body = <SettingsSkeleton tall />;
  } else if (status === "error") {
    body = (
      <div className="hx-empty hx-set-empty" role="alert">
        Couldn't read this device's settings.
        <button type="button" className="hx-btn" onClick={() => setAttempt(attempt + 1)}>
          Try again
        </button>
      </div>
    );
  } else {
    body = (
      <ul className="hx-set-sec-list">
        {SWITCHES.map((item) => (
          <SwitchRow key={item.key} item={item} checked={values[item.key]} onToggle={() => toggle(item)} />
        ))}
        <LinkRow
          image="wired_sensor"
          title="Wired Sensor Type"
          value={WIRED_TYPES[values.wired_type].label}
          onOpen={() => openSheet("wired")}
        />
        <LinkRow
          image="siren_duration"
          title="Siren Duration Time"
          value={`${values.siren_duration} sec`}
          onOpen={() => openSheet("siren")}
        />
        <LinkRow image="store_number" title="Store Alarm Phone Number" onOpen={() => openSheet("numbers")} />
        <LinkRow image="phone" title="Phone Number of Alarm System" onOpen={() => openSheet("alarm")} />
        <LinkRow image="exit_entry_delay" title="Exit / Entry Delay" onOpen={() => openSheet("delay")} />
      </ul>
    );
  }

  return (
    <SettingsPage title="Settings" device={device} busy={busy}>
      {body}

      {values && open === "wired" ? (
        <WiredSheet value={values.wired_type} onClose={closeSheet} onPick={pickWired} />
      ) : null}
      {values && open === "siren" ? (
        <SirenSheet value={values.siren_duration} onClose={closeSheet} onSave={saveSiren} />
      ) : null}
      {values && open === "numbers" ? (
        <NumbersSheet numbers={values.numbers} onClose={closeNumbers} onEdit={editSlot} />
      ) : null}
      {values && open === "numbers" && slot !== null ? (
        <NumberDialog
          key={slot}
          name={SLOTS[slot].name}
          stored={values.numbers[slot]}
          onClose={() => setSlot(null)}
          onSave={saveNumber}
          onDelete={deleteNumber}
        />
      ) : null}
      {values && open === "alarm" ? (
        <AlarmNumberDialog value={values.alarm_phone_number} onClose={closeSheet} onSave={saveAlarmNumber} />
      ) : null}
      {values && open === "delay" ? (
        <DelaySheet exit={values.exit_delay} entry={values.entry_delay} onClose={closeSheet} onSave={saveDelays} />
      ) : null}
    </SettingsPage>
  );
}

export default SecuritySettings;
